/**
 * Utility functions for formatting property data
 */

/**
 * Format property operation status to human-readable text.
 * Accepts a string (legacy) or an array (new multi-type).
 */
export const formatPropertyStatus = (status) => {
  if (Array.isArray(status)) {
    return status.map((s) => OPERATION_TYPE_LABELS[s] || s).join(" · ");
  }
  const statusMap = {
    preventa: "Preventa",
    venta: "Venta",
    renta: "Renta",
    // Legacy values for existing DB data
    venta_o_renta: "Venta · Renta",
    venta_nueva: "Venta",
    reventa: "Venta",
    renta_opcion_compra: "Renta",
    renta_venta_nueva: "Venta · Renta",
    renta_venta_reventa: "Venta · Renta",
    ambas: "Venta · Renta",
    en_venta: "Venta",
  };
  return (
    statusMap[status] ||
    (status ? status.charAt(0).toUpperCase() + status.slice(1) : "")
  );
};

/**
 * Format operation_types array or legacy status string to display label.
 */
export const formatOperationTypes = (operationTypes, status) => {
  if (Array.isArray(operationTypes) && operationTypes.length > 0) {
    return operationTypes.map((t) => OPERATION_TYPE_LABELS[t] || t).join(" · ");
  }
  return formatPropertyStatus(status);
};

/** Map from raw value to display label */
export const OPERATION_TYPE_LABELS = {
  preventa: "Preventa",
  venta: "Venta",
  renta: "Renta",
};

export const formatEstado = (estado) => {
  const estadoMap = {
    nuevo: "Nuevo",
    segunda_mano: "Segunda Mano",
  };
  return estadoMap[estado] || estado || "";
};

export const PROPERTY_TYPES = {
  Residencial: [
    "Casa",
    "Departamento",
    "Edificio",
    "Cabaña",
    "Quinta",
    "Rancho",
    "Desarrollo",
    "Mixto",
    "Terreno",
  ],
  Comercial: [
    "Local Comercial",
    "Consultorio",
    "Hospital",
    "Plaza Comercial",
    "Centro Comercial",
    "Oficina",
    "Bodega Comercial",
    "Edificio",
    "Desarrollo",
    "Hotel",
    "Mixto",
    "Terreno",
  ],
  Industrial: [
    "Nave Industrial",
    "Bodega Industrial",
    "Parque Industrial",
    "Fabrica",
    "Terreno",
  ],
};

export const OPERATION_TYPES = [
  { value: "preventa", label: "Preventa" },
  { value: "venta", label: "Venta" },
  { value: "renta", label: "Renta" },
];

export const ESTADO_OPTIONS = [
  { value: "nuevo", label: "Nuevo" },
  { value: "segunda_mano", label: "Segunda Mano" },
];

export const CATEGORIES = [
  { value: "Residencial", label: "Residencial" },
  { value: "Comercial", label: "Comercial" },
  { value: "Industrial", label: "Industrial" },
];

/** Supported listing currencies */
export const CURRENCIES = [
  { value: "MXN", label: "MXN", symbol: "$" },
  { value: "USD", label: "USD", symbol: "$" },
  { value: "EUR", label: "EUR", symbol: "€" },
  { value: "GBP", label: "GBP", symbol: "£" },
  { value: "CHF", label: "CHF", symbol: "CHF " },
];

export const PRICE_ON_REQUEST_LABEL = "Precio a consultar";

const CURRENCY_SYMBOLS = {
  MXN: "$",
  USD: "$",
  EUR: "€",
  GBP: "£",
  CHF: "CHF ",
};

/**
 * Format a price amount with its currency label.
 * Shows compact millions format for amounts ≥ 1M.
 */
export const formatPrice = (amount, currency = "MXN") => {
  if (amount == null || amount === "") return "N/A";
  const sym = CURRENCY_SYMBOLS[currency] || "$";
  if (amount >= 1_000_000) {
    return `${sym}${(amount / 1_000_000).toFixed(2)}M ${currency}`;
  }
  return `${sym}${Number(amount).toLocaleString("es-MX")} ${currency}`;
};

/**
 * Format price per m² given a total price, area, and currency.
 */
export const formatPricePerM2 = (amount, size, currency = "MXN") => {
  if (!amount || !size) return null;
  const sym = CURRENCY_SYMBOLS[currency] || "$";
  return `${sym}${Math.round(amount / size).toLocaleString("es-MX")} ${currency}/m²`;
};

/**
 * Extract the canonical price and currency from a property,
 * supporting both the new single-currency model and legacy price_mxn/price_usd.
 */
export const getPropertyPrice = (property) => {
  if (property.price != null) {
    return { price: property.price, currency: property.currency || "MXN" };
  }
  if (property.price_mxn != null) {
    return { price: property.price_mxn, currency: "MXN" };
  }
  if (property.price_usd != null) {
    return { price: property.price_usd, currency: "USD" };
  }
  return { price: null, currency: "MXN" };
};

export const isPriceOnRequest = (property) =>
  Boolean(property?.price_on_request);

export const formatPropertyPrice = (property) => {
  if (isPriceOnRequest(property)) return PRICE_ON_REQUEST_LABEL;
  const { price, currency } = getPropertyPrice(property);
  return formatPrice(price, currency);
};

/** @deprecated Use formatPrice instead */
export const formatCurrency = (amount, currency = "MXN") =>
  formatPrice(amount, currency);

/**
 * Format request status to human-readable text with color
 * @param {string} status - Request status (active, in_process, resolved, closed)
 * @returns {object} Object with label and color
 */
export const formatRequestStatus = (status) => {
  const statusConfig = {
    active: { label: "Activa", color: "green" },
    in_process: { label: "En Proceso", color: "blue" },
    resolved: { label: "Resuelta", color: "purple" },
    closed: { label: "Cerrada", color: "gray" },
  };
  return statusConfig[status] || { label: status, color: "gray" };
};
